/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Output,
	ViewEncapsulation,
	computed,
	inject,
	input,
	output,
	signal,
} from "@angular/core";
import { ActionLoggerService } from "@app/@services/generic/action-logger.service";
import { Subject, type Subscription } from "rxjs";
import { TooltipModule } from "../floating/tooltip/tooltip.module";
import { IconComponent } from "../icon/icon.component";

export interface CbSubmitEvent {
	success: Subject<void>;
	error: Subject<
		boolean | string | { message: string; args: Record<string, unknown> } | null
	>;
	reset: Subject<void>;
}

@Component({
	selector: "cb-button",
	imports: [CommonModule, IconComponent, TooltipModule],
	templateUrl: "./button.component.html",
	styleUrl: "./button.component.scss",
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent {
	private readonly actionLoggerService: ActionLoggerService =
		inject(ActionLoggerService);

	public readonly size = input<"xs" | "sm" | "md" | "lg" | "xl" | "2xl">("md");
	public readonly iconPosition = input<"left" | "right">("left");
	public readonly textAlignment = input<"left" | "right" | "center">("center");
	public readonly hierarchy = input<
		("primary" | "secondary" | "tertiary") | undefined
	>("primary");

	public readonly color = input<
		"brand" | "gray" | "white" | "error" | "warning" | "success" | "info"
	>("brand");
	public readonly icon = input<string | undefined>("");
	public readonly label = input<string>();
	public readonly type = input<"default" | "submit" | "iconOnly">("default");
	public readonly disabled = input(false);

	public readonly loading = input(false);
	public readonly resetAfterSubmit = input(true);

	public readonly click = output<MouseEvent>();
	@Output() public submit: EventEmitter<CbSubmitEvent> =
		new EventEmitter<CbSubmitEvent>();

	public status = signal<"idle" | "loading" | "success" | "error">("idle");
	public resolveSubcription?: Subscription;
	public resetSubcription?: Subscription;
	public rejectSubcription?: Subscription;

	public canClick = computed(() => {
		return !this.disabled() && this.status() === "idle";
	});

	public currColor = computed<ReturnType<typeof this.color>>(() => {
		switch (this.status()) {
			case "loading":
				return "gray";
			case "success":
				return "success";
			case "error":
				return "error";
		}

		return this.color();
	});

	public buttonType = computed(() => {
		return this.loading() ? "neutral" : this.type();
	});

	private registerStatusUpdate(status: string): void {
		const type = this.type();
		if (type === "submit") {
			const icon = this.icon();
			this.actionLoggerService.registerAction(
				`Button "${this.label()}"${icon ? ` with icon "${icon}"` : ""} is "${status}"`,
			);
		} else if (type === "iconOnly") {
			this.actionLoggerService.registerAction(
				`Button with icon "${this.icon()}" is "${status}"`,
			);
		} else {
			const icon = this.icon();
			this.actionLoggerService.registerAction(
				`Button "${this.label()}"${icon ? ` with icon "${icon}"` : ""} is "${status}"`,
			);
		}
	}

	/**
	 * Triggers when the user clicks on the button.
	 * @param clickEvent
	 * @returns
	 */
	public onClick(clickEvent?: MouseEvent): void {
		/* if (clickEvent) {
			clickEvent.stopImmediatePropagation();
		} */

		if (!this.canClick()) {
			if (clickEvent) {
				clickEvent.preventDefault();
				clickEvent.stopImmediatePropagation();
			}
			return;
		}

		this.registerStatusUpdate("clicked");
		//this.click.emit(clickEvent);
	}

	public manualClick(): void {
		if (this.type() === "submit") {
			this.clickSubmit();
		}

		this.onClick();
	}

	public clickSubmit(clickEvent?: MouseEvent): void {
		if (clickEvent) clickEvent.stopImmediatePropagation();

		if (this.disabled() || this.loading()) {
			if (clickEvent) clickEvent.preventDefault();
			return;
		}

		this.status.set("loading");
		this.registerStatusUpdate("loading");

		const resolveSubj = new Subject<void>();
		const rejectSubj = new Subject<
			| string
			| boolean
			| { message: string; args: Record<string, unknown> }
			| null
		>();
		const resetSubj = new Subject<void>();

		this.resolveSubcription = resolveSubj.subscribe(() => {
			this.status.set("success");
			this.registerStatusUpdate("succesful");

			if (this.resetAfterSubmit()) {
				setTimeout(() => this.reset(), 1500);
			}

			this.resolveSubcription?.unsubscribe();
			this.rejectSubcription?.unsubscribe();
			this.resetSubcription?.unsubscribe();
		});

		this.rejectSubcription = rejectSubj.subscribe(() => {
			this.status.set("error");
			this.registerStatusUpdate("failed");
			setTimeout(() => this.reset(), 1500);

			this.resolveSubcription?.unsubscribe();
			this.rejectSubcription?.unsubscribe();
			this.resetSubcription?.unsubscribe();
		});

		this.resetSubcription = resetSubj.subscribe(() => {
			this.reset();

			this.resolveSubcription?.unsubscribe();
			this.rejectSubcription?.unsubscribe();
			this.resetSubcription?.unsubscribe();
		});

		this.submit.emit({
			success: resolveSubj,
			error: rejectSubj,
			reset: resetSubj,
		});
	}

	public reset(): void {
		this.status.set("idle");
	}
}
